import { useEffect, useMemo, useState } from "react";
import type { CallRow } from "../../../../lib/calls";
import type { CrmLabel } from "../../../../lib/crmLabels";
import type { RowMenuProps } from "../../../shell/ContextMenu";
import { CallItem } from "../CallItem";
import { CallThreadRow } from "./CallThreadRow";
import { groupCallsByNumber, threadKeyOf } from "./group";

/**
 * The call log with every number folded onto one line.
 *
 * The same rows as the flat list, in the same order, standing in the same
 * column. Only the shape changes, which is why this is a second renderer next
 * to `CallItem` rather than a second list: an operator switching the toggle has
 * to recognise the screen they were just looking at.
 *
 * A THREAD OF ONE IS DRAWN AS A PLAIN ROW. Most of a busy log is one call per
 * number, and giving each of those a disclosure arrow that opens onto a single
 * row would add a click to the common case to serve the rare one.
 *
 * Threads start SHUT. Opening them all by default would draw the flat list with
 * extra furniture in it, which is the one thing the toggle is not for.
 *
 * The exception is the thread holding the call the detail pane has open. The
 * bell and every shared link point straight at a call id, so that row has to be
 * on screen: a detail pane beside a list the call is nowhere in reads as the
 * list having lost it. It opens once, when the open call moves to a different
 * thread, and not again - after that the operator is in charge of the group,
 * and a list refresh must not undo a fold they just closed.
 */
export function CallThreads({
  rows,
  openId,
  labels,
  onOpen,
  rowMenuProps,
}: {
  rows: CallRow[];
  openId: string | null;
  labels: CrmLabel[];
  onOpen: (id: string) => void;
  /** The list's one shared context menu, bound to a row. */
  rowMenuProps: (call: CallRow) => RowMenuProps;
}) {
  const threads = useMemo(() => groupCallsByNumber(rows), [rows]);
  const [open, setOpen] = useState<ReadonlySet<string>>(() => new Set());

  // A string, so the effect runs when the open call changes THREAD - not on
  // every poll that hands back the same rows in a new array.
  const holder = threadKeyOf(threads, openId);
  useEffect(() => {
    if (!holder) return;
    setOpen((current) => {
      if (current.has(holder)) return current;
      const next = new Set(current);
      next.add(holder);
      return next;
    });
  }, [holder]);

  function toggle(key: string): void {
    setOpen((current) => {
      const next = new Set(current);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  }

  return (
    <>
      {threads.map((thread) => {
        const first = thread.rows[0]!;
        if (thread.rows.length === 1) {
          return (
            <CallItem
              key={thread.key}
              call={first}
              open={first.id === openId}
              labels={labels}
              onOpen={onOpen}
              menuProps={rowMenuProps(first)}
            />
          );
        }

        const expanded = open.has(thread.key);
        return (
          <div key={thread.key} className="crm-group">
            <CallThreadRow
              thread={thread}
              open={expanded}
              onToggle={() => toggle(thread.key)}
              active={thread.rows.some((row) => row.id === openId)}
            />
            {/* Unmounted rather than hidden: a shut thread costs nothing, and
                the rows inside carry label chips and hover handlers that have
                no business existing behind a closed fold. */}
            {expanded ? (
              <div className="crm-group-kids">
                {thread.rows.map((call) => (
                  <CallItem
                    key={call.id}
                    call={call}
                    open={call.id === openId}
                    labels={labels}
                    onOpen={onOpen}
                    menuProps={rowMenuProps(call)}
                  />
                ))}
              </div>
            ) : null}
          </div>
        );
      })}
    </>
  );
}
